import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/AuthContext.jsx';
import { useRealtime, useRealtimeEvent } from '../lib/RealtimeContext.jsx';
import { Avatar, useToast } from './ui.jsx';
import Icon from './Icon.jsx';
import { formatTime, formatDayLabel, firstName } from '../lib/format.js';

/** Typing pings are throttled — one per this many ms while the user is typing. */
const TYPING_PING_MS = 2500;
const TYPING_CLEAR_MS = 4000;

const dayOf = (iso) => (iso || '').slice(0, 10);

export default function BookingChat({ booking }) {
  const { user } = useAuth();
  const toast = useToast();
  const { status, clearUnread } = useRealtime();

  const [state, setState] = useState(null); // { messages, canSend, other }
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [peerTyping, setPeerTyping] = useState(false);

  const listRef = useRef(null);
  const stickToBottom = useRef(true);
  const lastPing = useRef(0);
  const typingTimer = useRef(null);

  const bookingId = booking.id;

  const load = useCallback(async () => {
    try {
      const data = await api.get(`/api/bookings/${bookingId}/messages`);
      setState(data);
      clearUnread(bookingId);
    } catch (err) {
      toast.error(err);
    }
  }, [bookingId, clearUnread, toast]);

  useEffect(() => {
    load();
  }, [load]);

  // Live inbound messages for this conversation.
  useRealtimeEvent('message', (msg) => {
    if (msg.booking_id !== bookingId) return;
    setState((s) => {
      if (!s || s.messages.some((m) => m.id === msg.id)) return s;
      return { ...s, messages: [...s.messages, msg] };
    });
    setPeerTyping(false);
    if (!msg.self) {
      // We are looking at the thread, so mark it read straight away.
      api.get(`/api/bookings/${bookingId}/messages`).catch(() => {});
      clearUnread(bookingId);
    }
  });

  useRealtimeEvent('read', (data) => {
    if (data.bookingId !== bookingId) return;
    setState((s) =>
      s
        ? {
            ...s,
            messages: s.messages.map((m) =>
              m.sender_id === user.id && !m.read_at ? { ...m, read_at: new Date().toISOString() } : m
            ),
          }
        : s
    );
  });

  useRealtimeEvent('typing', (data) => {
    if (data.bookingId !== bookingId || data.from === user.id) return;
    setPeerTyping(true);
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => setPeerTyping(false), TYPING_CLEAR_MS);
  });

  useEffect(() => () => clearTimeout(typingTimer.current), []);

  // Keep the newest message in view, but never yank the scroll position away
  // from someone who has deliberately scrolled up to read history.
  useLayoutEffect(() => {
    const el = listRef.current;
    if (el && stickToBottom.current) el.scrollTop = el.scrollHeight;
  }, [state?.messages.length, peerTyping]);

  const onScroll = () => {
    const el = listRef.current;
    if (!el) return;
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };

  const onDraftChange = (e) => {
    setDraft(e.target.value);
    const now = Date.now();
    if (e.target.value && now - lastPing.current > TYPING_PING_MS) {
      lastPing.current = now;
      api.post(`/api/bookings/${bookingId}/typing`).catch(() => {});
    }
  };

  const send = async (e) => {
    e?.preventDefault();
    const body = draft.trim();
    if (!body || sending) return;

    setSending(true);
    setDraft('');
    stickToBottom.current = true;
    try {
      const saved = await api.post(`/api/bookings/${bookingId}/messages`, { body });
      // The stream echoes this back too; de-duplicate on id.
      setState((s) =>
        s && !s.messages.some((m) => m.id === saved.id)
          ? { ...s, messages: [...s.messages, saved] }
          : s
      );
    } catch (err) {
      toast.error(err);
      setDraft(body); // give the text back rather than losing it
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!state) {
    return (
      <section className="card">
        <div className="card-header">
          <h2>Messages</h2>
        </div>
        <div className="card-body stack-sm">
          <div className="skeleton skeleton-text" style={{ width: '55%' }} />
          <div className="skeleton skeleton-text" style={{ width: '38%' }} />
          <div className="skeleton skeleton-text" style={{ width: '62%' }} />
        </div>
      </section>
    );
  }

  const { messages, canSend, other } = state;

  return (
    <section className="card chat">
      <div className="card-header">
        <Avatar name={other.name} size="sm" />
        <div className="grow">
          <h2 style={{ fontSize: '1rem' }}>{other.name}</h2>
          <div className="tiny dim">
            {peerTyping ? (
              <span style={{ color: 'var(--accent)' }}>typing…</span>
            ) : status === 'open' ? (
              <span className="row-tight">
                <span className="live-dot" aria-hidden="true" /> connected
              </span>
            ) : status === 'reconnecting' || status === 'connecting' ? (
              'connecting…'
            ) : (
              'offline — messages will send when you reconnect'
            )}
          </div>
        </div>
      </div>

      <div className="chat-log" ref={listRef} onScroll={onScroll} role="log" aria-live="polite">
        {messages.length === 0 ? (
          <div className="center" style={{ margin: 'auto', maxWidth: 300 }}>
            <span className="icon-tile icon-tile-lg" style={{ margin: '0 auto var(--s-3)' }}>
              <Icon name="message" />
            </span>
            <p className="tiny dim">
              No messages yet. Ask about access, parking, medication or anything else about the visit.
            </p>
          </div>
        ) : (
          messages.map((m, i) => {
            const mine = m.sender_id === user.id;
            const newDay = dayOf(m.created_at) !== dayOf(messages[i - 1]?.created_at);
            const lastOfMine = mine && !messages.slice(i + 1).some((n) => n.sender_id === user.id);
            return (
              <div key={m.id}>
                {newDay && <div className="chat-day">{formatDayLabel(dayOf(m.created_at))}</div>}
                <div className={`bubble-row ${mine ? 'mine' : ''}`}>
                  <div className="bubble">
                    {m.body}
                    <span className="bubble-meta">
                      {formatTime(m.created_at.slice(11, 16))}
                      {lastOfMine && <Icon name={m.read_at ? 'check-circle' : 'check'} size={12} />}
                      {lastOfMine && (m.read_at ? 'Read' : 'Sent')}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
        {peerTyping && (
          <div className="bubble-row">
            <div className="bubble typing" aria-label={`${other.name} is typing`}>
              <span />
              <span />
              <span />
            </div>
          </div>
        )}
      </div>

      {canSend ? (
        <form className="chat-composer" onSubmit={send}>
          <textarea
            value={draft}
            onChange={onDraftChange}
            onKeyDown={onKeyDown}
            placeholder={`Message ${firstName(other.name)}…`}
            rows={1}
            aria-label="Message"
            maxLength={2000}
          />
          <button className="btn btn-primary" disabled={!draft.trim() || sending} aria-label="Send message">
            {sending ? <span className="spinner" /> : <Icon name="send" size={17} />}
          </button>
        </form>
      ) : (
        <div className="card-footer">
          <span className="tiny dim">
            This conversation is closed because the booking was {state.status}. History stays available.
          </span>
        </div>
      )}
    </section>
  );
}
